class Node {
  constructor() {
    this.parent = null
  }

  isPositionWithin(position) {
    return this.start !== null && this.end !== null && position >= this.start && position < this.end
  }

  findNodeAtPosition(position) {
    return this.isPositionWithin(position) ? this : null
  }

  ancestory(stopAfterClasses = null) {
    let head = this
    const result = []
    while (head !== null) {
      result.push(head)
      if (stopAfterClasses && stopAfterClasses.some(c => head instanceof c)) {
        break
      }
      head = head.parent
    }
    return result
  }

  toJSON() {
    return null
  }
}

function findNodeAtPositionForParentNode(position, node) {
  if (node.isPositionWithin(position)) {
    for (const childNode of node.childNodes) {
      const found = childNode.findNodeAtPosition(position)
      if (found !== null) {
        return found
      }
    }
    return node
  } else {
    return null
  }
}

/**
 Tokens:
 - Symbol: created_at, =, >=, etc.
 - String: "hello world"
 - Number: 123
 - Group: ( ... )
 - Label (later): [acme]
 - Separator i.e. comma
 - Whitespace
 */
export class Token extends Node {
  constructor({ type, value, start = null, end = null }) {
    super()
    this.type = type
    this.value = value
    this.start = start
    this.end = end
  }

  toJSON() {
    if (this.type === 'symbol') {
      return { nqlSym: this.value }
    } else if (this.type === 'string' || this.type === 'number') {
      return this.value
    } else {
      return undefined
    }
  }
}

export class GroupToken extends Token {
  constructor({ value, start = null, end = null, initiator = null, terminator = null }) {
    super({
      type: 'group',
      value,
      start,
      end
    })
    this.initiator = initiator
    this.terminator = terminator
    for (const token of value) {
      token.parent = this
    }
  }

  findNodeAtPosition(position) {
    return findNodeAtPositionForParentNode(position, this)
  }

  get childNodes() {
    return this.value
  }

  toJSON() {
    return this.value
      .map(v => {
        return v.toJSON()
      })
      .filter(v => v)
  }
}

export function parseNextToken(str, start = 0, end = null) {
  end = end || str.length

  if (start >= end) {
    return null
  }

  const testStr = str.substring(start, end)

  const tokenFromRegex = (regex, tokenType, tokenParser = null) => {
    const match = regex.exec(testStr)
    if (match) {
      const next = start + match[0].length
      return new Token({
        type: tokenType,
        value: tokenParser ? tokenParser(match[0]) : match[0],
        start: start,
        end: next
      })
    }
  }

  // whitespace
  const whitespaceToken = tokenFromRegex(/^\s+/, 'whitespace')
  if (whitespaceToken) {
    return whitespaceToken
  }

  // separator
  const separator = ','
  if (str[start] === separator) {
    return new Token({
      type: 'separator',
      value: separator,
      start: start,
      end: start + 1
    })
  }

  // dates (parse as a string token)
  const dateToken = tokenFromRegex(/^[0-9]{4}-[0-9]{2}-[0-9]{2}/, 'string')
  if (dateToken) {
    return dateToken
  }

  // numbers
  const numberToken = tokenFromRegex(/^-?[0-9]+(?:[.][0-9]+)?/, 'number', parseFloat)
  if (numberToken) {
    return numberToken
  }

  // symbols
  const symbolToken = tokenFromRegex(/^(?:\w|_|=|>|<|~|!)+/, 'symbol')
  if (symbolToken) {
    return symbolToken
  }

  // strings
  const stringRegex = /^(?:"([^"]*)"|'([^']*)')/
  const stringMatch = stringRegex.exec(testStr)
  if (stringMatch) {
    const next = start + stringMatch[0].length
    return new Token({
      type: 'string',
      value: stringMatch[1] || stringMatch[2],
      start: start,
      end: next
    })
  }

  // groups
  if (str[start] === '(') {
    const groupTokens = parseTokens(str, start + 1, end, ')')
    const groupTokensEnd =
      groupTokens.length > 0 ? groupTokens[groupTokens.length - 1].end : start + 1
    const groupEnd = str[groupTokensEnd] === ')' ? groupTokensEnd + 1 : groupTokensEnd
    return new GroupToken({
      value: groupTokens,
      start: start,
      end: groupEnd,
      initiator: '(',
      terminator: groupEnd > groupTokensEnd ? ')' : null
    })
  }

  return null
}

export function parseTokens(str, start = 0, end = null, terminator = null) {
  end = end || str.length
  const tokens = []
  // eslint-disable-next-line no-constant-condition
  while (start < end && (terminator === null || str[start] !== terminator)) {
    let token = parseNextToken(str, start, end)
    if (token === null) {
      break
    }
    tokens.push(token)
    start = token.end
  }
  return tokens
}

// AST
export class Expression extends Node {
  static setParent(x, parent) {
    if (x instanceof Token || x instanceof Expression) {
      x.parent = parent
    }
  }

  isPositionWithin(position) {
    return this.start !== null && this.end !== null && position >= this.start && position < this.end
  }

  findNodeAtPosition(position) {
    return findNodeAtPositionForParentNode(position, this)
  }
}

export class SimpleExpression extends Expression {
  constructor(field, operator, values) {
    super()
    Expression.setParent(field, this)
    this.field = field
    Expression.setParent(operator, this)
    this.operator = operator
    Expression.setParent(values, this)
    this.values = values
  }

  get start() {
    return this.field && this.field.start
  }

  get end() {
    return this.values ? this.values.end : this.operator ? this.operator.end : this.field.end
  }

  get childNodes() {
    return [this.field, this.operator, this.values].filter(n => n)
  }

  edgeForNode(node) {
    if (node === this.field) {
      return 'field'
    } else if (node === this.operator) {
      return 'operator'
    } else if (node === this.values) {
      return 'values'
    } else {
      return null
    }
  }

  toJSON() {
    return {
      nqlExp: [
        'SimpleExpression',
        this.operator && this.operator.value,
        this.field.toJSON(),
        this.values && this.values.toJSON()
      ]
    }
  }
}

export class CompoundExpression extends Expression {
  constructor(operator, left, right) {
    super()
    Expression.setParent(operator, this)
    this.operator = operator
    Expression.setParent(left, this)
    this.left = left
    Expression.setParent(right, this)
    this.right = right
  }

  get start() {
    return this.left ? this.left.start : this.operator.start
  }

  get end() {
    return this.right ? this.right.end : this.operator.end
  }

  get childNodes() {
    return [this.left, this.operator, this.right].filter(n => n)
  }

  toJSON() {
    return {
      nqlExp: [
        'CompoundExpression',
        this.operator.value,
        this.left && this.left.toJSON(),
        this.right && this.right.toJSON()
      ]
    }
  }
}

export class OrderByExpression extends Expression {
  constructor(prefix, fields) {
    super()
    Expression.setParent(prefix, this)
    this.prefix = prefix
    fields.forEach(field => {
      Expression.setParent(field, this)
    })
    this.fields = fields
  }

  get start() {
    return this.prefix.start
  }

  get end() {
    return this.fields[this.fields.length - 1].end
  }

  get childNodes() {
    return [this.prefix, ...this.fields].filter(n => n)
  }

  toJSON() {
    return this.fields.map(field => {
      if (field instanceof GroupToken) {
        // return the JSON value for each field to match what is expected by the backend
        let fieldName = field.value[0].value
        if (field.value.length === 2) {
          const dir = field.value[1].value
          if (dir.toLowerCase() === 'desc') {
            fieldName = '-' + fieldName
          }
        }
        return fieldName
      } else {
        return field.value
      }
    })
  }
}

export class OrderedExpression extends Expression {
  constructor(filter, orderBy) {
    super()
    if (filter) {
      Expression.setParent(filter, this)
      this.filter = filter
    } else {
      this.filter = null
    }
    Expression.setParent(orderBy, this)
    this.orderBy = orderBy
  }

  get start() {
    return this.filter ? this.filter.start : this.orderBy.start
  }

  get end() {
    return this.orderBy.end
  }

  get childNodes() {
    return [this.filter, this.orderBy].filter(n => n)
  }

  toJSON() {
    return {
      nqlExp: ['OrderedExpression', this.filter && this.filter.toJSON(), this.orderBy.toJSON()]
    }
  }
}

const AST_STATES = {
  EXPRESSION_START: 'expression_start',
  EXPESSION_END: 'expression_end'
}

function ast(tokens, tstart = 0, tend = null, options = null) {
  tend = tend || tokens.length
  let state = AST_STATES.EXPRESSION_START
  let result = null
  while (tstart < tend) {
    if (state === AST_STATES.EXPRESSION_START) {
      const expInfo = astExpressionStart(tokens, tstart, tend, options)
      if (expInfo === null) {
        break
      }
      result = expInfo.expression
      tstart = expInfo.tend
      state = AST_STATES.EXPESSION_END
    } else if (state === AST_STATES.EXPESSION_END) {
      const expInfo = astExpressionEnd(tokens, result, tstart, tend, options)
      if (expInfo === null) {
        break
      }
      result = expInfo.expression
      tstart = expInfo.tend
      state = AST_STATES.EXPESSION_END
    } else {
      throw new Error(`Reached an unknown state: ${state}`)
    }
  }
  return result
}

function astSimpleExpression(tokens, tstart, tend, options = null) {
  let field = tokens[tstart],
    operator = null,
    values = null

  tstart++

  do {
    tstart = astSkipWhitespaceTokens(tokens, tstart, tend)
    if (tstart >= tend) {
      break
    }
    operator = tokens[tstart]

    if (operator.type === 'symbol' && tstart + 2 < tend) {
      let localTstart = tstart + 1
      const next = tokens[localTstart]
      if (next.type === 'whitespace') {
        localTstart++
        const next2 = tokens[localTstart]
        if (
          next2.type === 'symbol' &&
          ((operator.value.toLowerCase() === 'not' && next2.value.toLowerCase() === 'in') ||
            (operator.value.toLowerCase() === 'is' && next2.value.toLowerCase() === 'not'))
        ) {
          operator.value += next.value
          operator.value += next2.value
          operator.end = next2.end
          tstart = localTstart
        }
      }
    }

    tstart++
    tstart = astSkipWhitespaceTokens(tokens, tstart, tend)
    if (tstart >= tend) {
      break
    }
    values = tokens[tstart]

    tstart++
    if (options && options.greedySymbolValues && values && values.type === 'symbol') {
      // now we have got a token with a symbol value, but can we get more?
      // keeping consuming following whitespaces and tokens until we encounter
      // 1. A non-symbol and a non-whitespace
      // 2. Or a symbol with the compound expression operators - AND, OR, NOT, ORDER
      while (tstart < tend) {
        const next = tokens[tstart]
        if (
          next.type === 'whitespace' ||
          (next.type === 'symbol' &&
            !['and', 'or', 'not', 'order'].includes(next.value.toLowerCase()))
        ) {
          tstart++
          values.value += next.value
          values.end = next.end
        } else {
          break
        }
      }
    }
    // eslint-disable-next-line no-constant-condition
  } while (false)

  return { expression: new SimpleExpression(field, operator, values), tend: tstart }
}

function astOrderBy(tokens, tstart, tend) {
  if (tstart >= tend) {
    return null
  }

  function nextSymbol(value) {
    tstart = astSkipWhitespaceTokens(tokens, tstart, tend)
    if (tstart >= tend) {
      return null
    }
    const next = tokens[tstart]
    if (next.type === 'symbol') {
      if (value === undefined || next.value.toLowerCase() === value.toLowerCase()) {
        tstart++
        return next
      }
    }
    return null
  }

  // parse prefix
  const orderSymbol = nextSymbol('order')
  const bySymbol = nextSymbol('by')
  if (orderSymbol === null || bySymbol === null) {
    return null
  }
  const prefixGroupToken = new GroupToken({
    value: [orderSymbol, bySymbol],
    start: orderSymbol.start,
    end: bySymbol.end
  })

  // parse fields until end
  let fields = []
  while (tstart < tend) {
    let fieldSymbol = nextSymbol()
    if (fieldSymbol === null) {
      return null
    }
    let directionSymbol = null
    tstart = astSkipWhitespaceTokens(tokens, tstart, tend)
    if (tstart < tend) {
      let next = tokens[tstart]
      if (next.type === 'symbol' && ['asc', 'desc'].includes(next.value.toLowerCase())) {
        directionSymbol = next
        tstart++
        tstart = astSkipWhitespaceTokens(tokens, tstart, tend)
        next = tokens[tstart]
      }
      if (tstart < tend && next.type === 'separator' && next.value === ',') {
        tstart++
      }
    }
    if (directionSymbol === null) {
      fields.push(fieldSymbol)
    } else {
      fields.push(
        new GroupToken({
          value: [fieldSymbol, directionSymbol],
          start: fieldSymbol.start,
          end: directionSymbol.end
        })
      )
    }
  }
  if (fields.length === 0) {
    return null
  }
  return {
    expression: new OrderByExpression(prefixGroupToken, fields),
    tend: tstart
  }
}

function astSkipWhitespaceTokens(tokens, tstart, tend) {
  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (tstart >= tend) {
      return tend
    }
    if (tokens[tstart].type === 'whitespace') {
      tstart++
    } else {
      break
    }
  }
  return tstart
}

function astExpressionStart(tokens, tstart, tend, options = null) {
  if (tstart >= tend) {
    return null
  }
  tstart = astSkipWhitespaceTokens(tokens, tstart, tend)
  if (tstart >= tend) {
    return null
  }
  const next = tokens[tstart]
  if (next.type === 'symbol') {
    const value = next.value.toLowerCase()
    if (value === 'not') {
      const rightExpInfo = astExpressionStart(tokens, tstart + 1, tend)
      return {
        expression: new CompoundExpression(next, null, rightExpInfo.expression),
        tend: rightExpInfo.tend
      }
    } else if (value === 'and' || value === 'or') {
      return null
    } else if (value === 'order') {
      const orderByInfo = astOrderBy(tokens, tstart, tend)
      if (orderByInfo) {
        return {
          expression: new OrderedExpression(null, orderByInfo.expression),
          tend: orderByInfo.tend
        }
      } else {
        return null
      }
    } else {
      return astSimpleExpression(tokens, tstart, tend, options)
    }
  } else if (next.type === 'group') {
    return {
      expression: ast(next.value),
      tend: tstart + 1
    }
  } else {
    return null
  }
}

function astExpressionEnd(tokens, previous, tstart, tend, options = null) {
  if (tstart >= tend) {
    return null
  }
  tstart = astSkipWhitespaceTokens(tokens, tstart, tend)
  if (tstart >= tend) {
    return null
  }
  const next = tokens[tstart]
  if (next.type === 'symbol') {
    const value = next.value.toLowerCase()
    if (value === 'and' || value === 'or') {
      const rightExpInfo = astExpressionStart(tokens, tstart + 1, tend, options)
      return {
        expression: new CompoundExpression(
          next,
          previous,
          rightExpInfo ? rightExpInfo.expression : null
        ),
        tend: rightExpInfo ? rightExpInfo.tend : tend
      }
    } else if (value === 'order') {
      const orderByInfo = astOrderBy(tokens, tstart, tend)
      if (orderByInfo) {
        return {
          expression: new OrderedExpression(previous, orderByInfo.expression),
          tend: orderByInfo.tend
        }
      } else {
        return null
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

export function parseNQL(str, start = 0, end = null, options = null) {
  const tokens = parseTokens(str, start, end)
  return ast(tokens, 0, tokens.length, options)
}

export function autoCompleteParams(str, position) {
  let debugId = new Date().getTime()
  console.log(debugId, 'autocomlpete', position, str)
  if (position <= 0 || position > str.length) {
    return null
  }

  const root = parseNQL(str, 0, position, { greedySymbolValues: true })
  if (root === null) {
    return null
  }

  const targetToken = root.findNodeAtPosition(position - 1)
  if (targetToken === null || !(targetToken instanceof Token) || targetToken.type !== 'symbol') {
    return null
  }

  const ancestory = targetToken.ancestory([SimpleExpression, OrderByExpression])
  const matchedExpression = ancestory[ancestory.length - 1]

  console.log(debugId, 'autocomplete target token', targetToken)
  console.log(debugId, 'autocomplete ancestory', ancestory)
  console.log(debugId, 'matched expression', matchedExpression)

  if (matchedExpression instanceof SimpleExpression) {
    const secondEarliestAncestor = ancestory[ancestory.length - 2]
    const edge = matchedExpression.edgeForNode(secondEarliestAncestor)

    if (edge === null) {
      return null
    }

    console.log(debugId, 'autocomplete simple expression result', {
      edge: edge,
      field: matchedExpression.field.value,
      value: targetToken.value,
      start: targetToken.start,
      end: targetToken.end
    })

    return {
      edge: edge,
      field: matchedExpression.field.value,
      value: targetToken.value,
      start: targetToken.start,
      end: targetToken.end
    }
  } else if (matchedExpression instanceof OrderByExpression) {
    if (matchedExpression.fields.includes(targetToken)) {
      return {
        edge: 'field',
        field: targetToken.value,
        value: targetToken.value,
        start: targetToken.start,
        end: targetToken.end
      }
    } else {
      return null
    }
  } else {
    return null
  }
}
